/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useCrmFilters.ts
 * PURPOSE: CRM hub filters backed by URL search params (replace navigation, shareable, refresh-safe)
 * DEPENDENCIES: react, react-router-dom, ../types/crm.types, ../utils/crmFilterUrl
 * PUBLIC_API: useCrmFilters, UseCrmFiltersResult, CrmUrlFilterProfile
 * SIDE_EFFECTS: Updates browser URL via useSearchParams replace
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 70/150
 * RECENT: 2026-05-11 [2.2.941] Vitest **`useCrmFilters.test.tsx`** uses **`waitFor`** + **`textContent`** after **`resetFilters`** (jest-dom **`toHaveTextContent('')`**); hook unchanged — root **`CHANGELOG` [2.2.941]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.884] **`tasks`** profile maps **`/crm/tasks`** — root **`CHANGELOG` [2.2.884]`**; 2026-04-30 [2.2.748] **`useSearchParams`** + **`crmFilterUrl`** — CHANGELOG [2.2.748]
 */

import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { CrmFilterState } from '../types/crm.types';
import {
  type CrmUrlFilterProfile,
  filtersFromSearchParams,
  setFilterOnSearchParams,
} from '../utils/crmFilterUrl';

export type { CrmUrlFilterProfile } from '../utils/crmFilterUrl';

export type UseCrmFiltersResult = {
  filters: CrmFilterState;
  setFilter: (key: keyof CrmFilterState, value: string) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
};

function computeHasActiveFilters(filters: CrmFilterState): boolean {
  if (filters.search.trim() !== '') return true;
  if (filters.owner) return true;
  if (filters.status) return true;
  if (filters.stage) return true;
  if (filters.territory) return true;
  if (filters.sort) return true;
  if (filters.region) return true;
  if (filters.taskDue && filters.taskDue !== 'all') return true;
  if (filters.dateRange?.from?.trim() || filters.dateRange?.to?.trim()) return true;
  return false;
}

export function useCrmFilters(profile: CrmUrlFilterProfile): UseCrmFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const filters = useMemo(
    () => filtersFromSearchParams(profile, searchParams),
    [profile, searchParams]
  );

  const setFilter = useCallback(
    (key: keyof CrmFilterState, value: string) => {
      /* CRM URL profiles do not persist dateRange in the query string. */
      if (key === 'dateRange') return;

      setSearchParams((prev) => setFilterOnSearchParams(profile, prev, key, value), { replace: true });
    },
    [profile, setSearchParams]
  );

  const resetFilters = useCallback(() => {
    navigate({ pathname, search: '' }, { replace: true });
  }, [navigate, pathname]);

  const hasActiveFilters = useMemo(() => computeHasActiveFilters(filters), [filters]);

  return { filters, setFilter, resetFilters, hasActiveFilters };
}
